﻿import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { createLogger } from '@/lib/logger';
import { hasAtLeast } from '@/lib/subscription';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Pressable,
  ScrollView,
  Animated,
  RefreshControl,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useFocusEffect } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useProfileStore } from '@/stores/useProfileStore';
import { supabase } from '@/lib/supabase';
import { fetchHomeDashboard, type HomeDashboardData } from '@/lib/homeDashboard';
import CoachHomeWidget from '@/components/CoachHomeWidget';
import { loadCoachPlan } from '@/lib/coachStorage';
import type { CoachWeekPlan } from '@/lib/coachEngine';
import OrgHomeWidget from '@/components/OrgHomeWidget';
import BaselineChecklist from '@/components/BaselineChecklist';
import PendingDuelBanner from '@/components/PendingDuelBanner';
import SeasonBanner from '@/components/SeasonBanner';
import LiveFeed from '@/components/LiveFeed';
import { useSubscription } from '@/hooks/useSubscription';
import { fetchBaselineStatus } from '@/lib/baselineClient';
import type { BaselineStatus } from '@/lib/baselineGate';
import {
  Trophy,
  Swords,
  ChevronRight,
  Zap,
  Clock,
  Dumbbell,
  BarChart3,
  MessageSquare,
  Info,
  Users,
  RotateCcw,
} from 'lucide-react-native';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, TRACKING, SPACE, CARD, SECTION_LABEL } from '@/constants/designTokens';
import TutorialOverlay from '@/components/TutorialOverlay';
import { captureError } from '@/lib/errorReporting';
import { useCoachInvites } from '@/hooks/useCoachInvites';
import CoachInviteModal from '@/components/CoachInviteModal';
import ClientMessageModal from '@/components/ClientMessageModal';
import WelcomeProModal, { useWelcomeProModal } from '@/components/WelcomeProModal';
import { isFlagEnabled } from '@/lib/featureFlags';
import { SLAM } from '@/lib/animations';
import AnimatedButton from '@/components/AnimatedButton';
import { useUserStage } from '@/hooks/useUserStage';
import { useUserGyms } from '@/hooks/useUserGyms';
import GymSwitcherPills from '@/components/GymSwitcherPills';
import InfoSheet from '@/components/InfoSheet';
import { INFO_CONTENT } from '@/lib/infoContent';
import ScoreHistoryChart from '@/components/ScoreHistoryChart';

const log = createLogger('HOME');

const STALE_MS = 5 * 60_000; // 5 min — avoids hammering DB on every tab switch
const BASELINE_LIFTS = ['back-squat', 'deadlift', 'bench-press', 'weighted-pull-up', 'dead-hang'];

const FRESHNESS_LABEL_MAP: Record<string, string> = {
  Fresh: 'UP TO DATE',
  Active: 'ACTIVE',
  Aging: 'NEEDS UPDATE',
  Stale: 'OUTDATED',
  Unranked: 'Unranked',
};

const BAND_COLORS: Record<string, string> = {
  Fresh: '#22c55e',
  'UP TO DATE': '#22c55e',
  Active: '#3b82f6',
  ACTIVE: '#3b82f6',
  Aging: '#f59e0b',
  'NEEDS UPDATE': '#f59e0b',
  Stale: '#ef4444',
  OUTDATED: '#ef4444',
  Unranked: '#6b7280',
};

export default function HomeDashboard() {
  const router = useRouter();
  const { theme } = useTheme();
  const { refreshProfile } = useAuth();
  const profile = useProfileStore((s) => s.profile);
  const { tier } = useSubscription();
  const c = theme.colors;
  const { showWelcomePro, markWelcomeProSeen } = useWelcomeProModal(hasAtLeast(tier, 'pro'));

  const { pendingInvites: coachInvites, acceptInvite, declineInvite } = useCoachInvites();

  const [dashboard, setDashboard] = useState<HomeDashboardData | null>(null);
  const [coachPlan, setCoachPlan] = useState<CoachWeekPlan | null>(null);
  const [baselineStatus, setBaselineStatus] = useState<BaselineStatus | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showMessages, setShowMessages] = useState(false);
  const [heroTranslateY] = useState(() => new Animated.Value(30));
  const [heroOpacity] = useState(() => new Animated.Value(0));
  const [buttonsScale] = useState(() => new Animated.Value(0.9));
  const lastFetchRef = useRef<number>(0);
  const [infoSheet, setInfoSheet] = useState<keyof typeof INFO_CONTENT | null>(null);
  const scoreAnimValue = useRef(new Animated.Value(0)).current;
  const [displayScore, setDisplayScore] = useState(0);
  const scoreAnimatedRef = useRef(false);

  // IRON SLAMMING entrance animations
  useEffect(() => {
    Animated.parallel([
      Animated.spring(heroTranslateY, { toValue: 0, ...SLAM }),
      Animated.spring(heroOpacity, { toValue: 1, ...SLAM })
    ]).start();

    setTimeout(() => {
      Animated.spring(buttonsScale, {
        toValue: 1.0,
        tension: 200,
        friction: 12,
        useNativeDriver: false
      }).start();
    }, 400);
  }, []);

  // ── Data loading ──
  const refreshAll = useCallback(async (force = false) => {
    if (!force && Date.now() - lastFetchRef.current < STALE_MS) return;
    try {
      if (refreshProfile) await refreshProfile();
      if (!profile?.id) return;

      // Fetch baseline + dashboard + coach plan in parallel
      const [bStatus, data, savedPlan] = await Promise.all([
        fetchBaselineStatus(profile.id).catch(() => null),
        fetchHomeDashboard(profile.id),
        loadCoachPlan(profile.id).catch(() => null),
      ]);
      if (savedPlan) setCoachPlan(savedPlan);
      if (bStatus) setBaselineStatus(bStatus);
      if (data) {
        setDashboard(data);
        setLoadError(null);
        lastFetchRef.current = Date.now();
        return;
      }

      // Fallback: minimal client-side queries
      const duelsRes = await supabase
        .from('duels')
        .select('id,status,duel_type,challenger_id,challenged_id,timeframe_hours,created_at,end_at')
        .or(`challenger_id.eq.${profile.id},challenged_id.eq.${profile.id}`)
        .in('status', ['pending', 'active'])
        .order('created_at', { ascending: false })
        .limit(10);

      // Build baseline from strength_events
      const baselineRes = await supabase.from('strength_events')
        .select('canonical_name,performed_at,weight_kg,reps')
        .eq('profile_id', profile.id)
        .in('canonical_name', BASELINE_LIFTS)
        .order('performed_at', { ascending: false });

      const seenLifts = new Set<string>();
      const baselineLifts: HomeDashboardData['baseline_lifts'] = [];
      for (const row of baselineRes.data ?? []) {
        if (!seenLifts.has(row.canonical_name)) {
          seenLifts.add(row.canonical_name);
          baselineLifts.push({ lift: row.canonical_name, logged_at: row.performed_at, weight: row.weight_kg, reps: row.reps });
        }
      }

      setDashboard({
        profile: {
          strength_score: profile?.duel_standard_strength_kg ?? 0,
          freshness_band: profile?.freshness_band ?? 'Unranked',
          tier: profile?.subscription_tier ?? 'free',
          units: profile?.units ?? 'lbs',
        },
        rank: null,
        recent_workouts: [],
        active_duels: duelsRes.data ?? [],
        baseline_lifts: baselineLifts,
        unread_coach_messages: 0,
        club: null,
      });
      setLoadError(null);
      lastFetchRef.current = Date.now();
    } catch (err) {
      log.warn('[HOME] refresh failed', err);
      captureError(err, { context: 'home.refreshAll' });
      setLoadError('Unable to load data. Pull down to refresh.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshProfile, profile?.id]);

  useFocusEffect(useCallback(() => { refreshAll(); return () => {}; }, [refreshAll]));

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refreshAll(true);
    setRefreshing(false);
  }, [refreshAll]);

  // ── Derived state ──
  const rawScore = dashboard?.profile?.strength_score ?? profile?.duel_standard_strength_kg ?? 0;
  const duelScore = Math.round(rawScore * 100) / 100;

  // Score counter animation
  useEffect(() => {
    if (duelScore > 0 && !scoreAnimatedRef.current) {
      scoreAnimatedRef.current = true;
      const listenerId = scoreAnimValue.addListener(({ value }) => {
        setDisplayScore(Math.round(value * 100) / 100);
      });
      Animated.timing(scoreAnimValue, {
        toValue: duelScore,
        duration: 1000,
        useNativeDriver: false,
      }).start(() => {
        setDisplayScore(duelScore);
        scoreAnimValue.removeListener(listenerId);
      });
    } else if (duelScore > 0 && scoreAnimatedRef.current) {
      setDisplayScore(duelScore);
    }
  }, [duelScore]);

  const rank = dashboard?.rank;
  const rawFreshnessLabel = dashboard?.profile?.freshness_band || profile?.freshness_band || 'Unranked';
  const freshnessLabel = FRESHNESS_LABEL_MAP[rawFreshnessLabel] || rawFreshnessLabel;
  const freshnessColor = BAND_COLORS[freshnessLabel] || BAND_COLORS[rawFreshnessLabel] || c.textTertiary;
  const baselineComplete = (dashboard?.baseline_lifts?.length ?? 0) >= 5 || duelScore > 0;
  const activeDuels = (dashboard?.active_duels ?? []).filter(d => {
    if (d.status !== 'active') return false;
    if (d.end_at && new Date(d.end_at).getTime() <= Date.now()) return false;
    if (d.timeframe_hours && d.created_at) {
      const endTime = new Date(d.created_at).getTime() + d.timeframe_hours * 3600000;
      if (endTime <= Date.now()) return false;
    }
    return true;
  });
  const pendingDuels = (dashboard?.active_duels ?? []).filter(d => {
    if (d.status !== 'pending') return false;
    // Only show challenges directed AT the current user (not ones they sent)
    if (d.challenged_id !== profile?.id) return false;
    // Hide stale pending duels (older than 7 days)
    if (d.created_at) {
      const age = Date.now() - new Date(d.created_at).getTime();
      if (age > 7 * 24 * 3600000) return false;
    }
    return true;
  });
  const unreadMsgCount = dashboard?.unread_coach_messages ?? 0;

  // ── Trial countdown ──
  const [trialBannerDismissed, setTrialBannerDismissed] = useState(false);
  const isOnTrial = profile?.subscription_tier === 'pro' && !!profile?.subscription_expires_at;
  const daysLeft = useMemo(() => {
    if (!profile?.subscription_expires_at) return 0;
    const diff = new Date(profile.subscription_expires_at).getTime() - Date.now();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  }, [profile?.subscription_expires_at]);

  // ── Greeting ──
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    const name = profile?.display_name?.split(' ')[0] || profile?.handle || 'Athlete';
    if (hour >= 5 && hour < 12) return `Good morning, ${name}`;
    if (hour >= 12 && hour < 17) return `Good afternoon, ${name}`;
    if (hour >= 17 && hour < 21) return `Good evening, ${name}`;
    return `Late night grind, ${name}`;
  }, [profile?.display_name, profile?.handle]);

  // ── User Stage (progressive disclosure) ──
  const { stage } = useUserStage(dashboard, profile);

  // ── Multi-gym support ──
  const { gyms, activeGym, setActiveGym } = useUserGyms();

  // ── Smart CTA ──
  const smartCTA = useMemo(() => {
    if (!baselineComplete && duelScore === 0) return { text: 'Log your 5 core lifts to get ranked', action: 'baseline' as const };
    if (rawFreshnessLabel === 'Stale') return { text: 'Your ranking is dropping — log a workout to stay current', action: 'record' as const };
    if (rawFreshnessLabel === 'Aging') return { text: 'Keep your ranking current — log a new workout', action: 'record' as const };
    if (activeDuels.length === 0 && baselineComplete && (profile?.home_gym_id || profile?.gym_id)) return { text: 'Challenge someone at your gym', action: 'duel_create' as const };
    if (activeDuels.length === 0 && baselineComplete && !profile?.home_gym_id && !profile?.gym_id) return { text: 'Set your home gym to find opponents', action: 'set_gym' as const };
    return null;
  }, [baselineComplete, rawFreshnessLabel, activeDuels.length, duelScore, profile?.home_gym_id, profile?.gym_id]);

  const handleSmartCTA = () => {
    switch (smartCTA?.action) {
      case 'baseline': router.push('/(tabs)/record' as any); break;
      case 'record': router.push('/(tabs)/record' as any); break;
      case 'duel_create': router.push({ pathname: '/(tabs)/duels', params: { start_duel: '1' } } as any); break;
      case 'set_gym': router.push('/(tabs)/edit-profile' as any); break;
    }
  };

  const showStrengthScoreInfo = () => setInfoSheet('strengthScore');

  return (
    <SafeAreaView style={{ backgroundColor: c.background, flex: 1 }} edges={['top']}>
      <Animated.View style={{ flex: 1, opacity: heroOpacity }}>
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={styles.container}
          showsVerticalScrollIndicator={false}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={c.accent} />}
        >
          {/* ── Greeting + Message Icon ── */}
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text style={[styles.greeting, { color: c.text }]} accessibilityRole="header">{greeting}</Text>
            <TouchableOpacity
              onPress={() => setShowMessages(true)}
              style={{ padding: 6, position: 'relative' }}
              accessibilityRole="button"
              accessibilityLabel={`Messages${unreadMsgCount > 0 ? `, ${unreadMsgCount} unread` : ''}`}
            >
              <MessageSquare size={22} color={c.textSecondary} />
              {unreadMsgCount > 0 && (
                <View style={styles.badge}>
                  <Text style={styles.badgeText}>{unreadMsgCount > 9 ? '9+' : unreadMsgCount}</Text>
                </View>
              )}
            </TouchableOpacity>
          </View>

          {/* ── Error Banner ── */}
          {loadError && !refreshing && (
            <View style={{ padding: 12, backgroundColor: '#ef444420', borderRadius: 4, borderWidth: 1, borderColor: '#ef444440' }}>
              <Text style={{ color: '#ef4444', fontSize: 13, fontFamily: TYPO.body.medium, textAlign: 'center' }}>
                {loadError}
              </Text>
            </View>
          )}

          {/* ── Pro Trial Countdown Banner ── */}
          {isOnTrial && daysLeft <= 7 && daysLeft > 0 && !trialBannerDismissed && (
            <Pressable
              onPress={() => router.push('/paywall' as any)}
              style={{ backgroundColor: '#F59E0B22', borderRadius: 6, padding: 12, marginBottom: 4, borderWidth: 1, borderColor: '#F59E0B44', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
              accessibilityRole="button"
              accessibilityLabel={`${daysLeft} day${daysLeft !== 1 ? 's' : ''} left on Pro trial, tap to upgrade`}
            >
              <Text style={{ color: '#F59E0B', fontWeight: '700', fontSize: 14, textAlign: 'center', flex: 1 }}>
                {daysLeft} day{daysLeft !== 1 ? 's' : ''} left on Pro trial — tap to upgrade
              </Text>
              <TouchableOpacity onPress={(e) => { e.stopPropagation?.(); setTrialBannerDismissed(true); }} style={{ padding: 4 }} accessibilityRole="button" accessibilityLabel="Dismiss trial banner">
                <Text style={{ color: '#F59E0B', fontSize: 16, fontWeight: '700' }}>×</Text>
              </TouchableOpacity>
            </Pressable>
          )}

          {/* ── Gym Switcher (multi-gym users) ── */}
          {gyms.length >= 2 && (
            <GymSwitcherPills
              gyms={gyms}
              activeGymId={activeGym?.id ?? null}
              onSelect={setActiveGym}
              colors={c}
            />
          )}

          {/* ── Pending Duel Banner (unmissable) ── */}
          {stage !== 'newcomer' && <PendingDuelBanner count={pendingDuels.length} />}

          {/* ── Newcomer Welcome Card ── */}
          {stage === 'newcomer' && (
            <View style={[styles.welcomeCard, { backgroundColor: c.surface, borderColor: c.accent + '40' }]}>
              <Text style={[styles.welcomeHeading, { color: c.accent }]}>WELCOME TO DUEL</Text>
              <View style={styles.welcomeSteps}>
                {[
                  { num: '1', label: 'LOG YOUR LIFTS', Icon: Dumbbell },
                  { num: '2', label: 'GET YOUR SCORE', Icon: Trophy },
                  { num: '3', label: 'CHALLENGE SOMEONE', Icon: Swords },
                ].map((step) => (
                  <View key={step.num} style={styles.welcomeStep}>
                    <View style={[styles.welcomeStepNum, { backgroundColor: c.accent }]}>
                      <Text style={styles.welcomeStepNumText}>{step.num}</Text>
                    </View>
                    <step.Icon size={18} color={c.accent} />
                    <Text style={[styles.welcomeStepLabel, { color: c.text }]}>{step.label}</Text>
                  </View>
                ))}
              </View>
              <AnimatedButton
                style={styles.welcomeCTA}
                onPress={() => router.push('/(tabs)/record' as any)}
                accessibilityRole="button"
                accessibilityLabel="Log your first workout"
              >
                <Dumbbell size={18} color="#030303" />
                <Text style={styles.welcomeCTAText}>LOG YOUR FIRST WORKOUT</Text>
              </AnimatedButton>
            </View>
          )}

          {/* ── Hero Card ── */}
          {stage !== 'newcomer' && <View style={[styles.heroCard, { backgroundColor: c.surface, borderColor: c.border }]}>
            <View style={styles.heroTop}>
              <View style={styles.heroScoreBlock}>
                <View style={styles.scoreHeader}>
                  <Text style={[styles.heroScoreLabel, { color: c.accent }]}>STRENGTH SCORE</Text>
                  <TouchableOpacity onPress={showStrengthScoreInfo} style={styles.infoIcon}>
                    <Info size={14} color={c.textSecondary} />
                  </TouchableOpacity>
                </View>
                <Text style={[styles.heroScoreValue, { color: c.text }]}>
                  {duelScore > 0 ? displayScore : '—'}
                </Text>
                {rank ? (
                  <Text style={[styles.rankContext, { color: c.textSecondary }]}>
                    {'#'}{rank.rank}{' at your gym'}{rank.total >= 5 && rank.percentile > 0 && rank.rank < rank.total ? ` · Top ${100 - rank.percentile + 1}%` : ''}
                  </Text>
                ) : null}
                {rank && rank.rank > 0 && rank.total > 0 && (() => {
                  const pct = Math.round((1 - rank.rank / rank.total) * 100);
                  // Hide percentile text when < 5 users (too volatile)
                  if (rank.total < 5) return null;
                  // Last place (rank === total) — don't show misleading "Top 100%"
                  if (rank.rank === rank.total) {
                    return (
                      <Text style={[styles.rankContext, { color: c.textTertiary, marginTop: 2 }]}>
                        Keep climbing!
                      </Text>
                    );
                  }
                  // Low percentile — encourage instead of "Stronger than 0%"
                  if (pct < 10) {
                    return (
                      <Text style={[styles.rankContext, { color: c.textTertiary, marginTop: 2 }]}>
                        Keep logging to climb the ranks
                      </Text>
                    );
                  }
                  return (
                    <Text style={[styles.rankContext, { color: c.textTertiary, marginTop: 2 }]}>
                      {pct >= 90 ? `🔥 Stronger than ${pct}% of your gym` : pct >= 75 ? `Top 25% at your gym` : `Stronger than ${pct}% of your gym`}
                    </Text>
                  );
                })()}
              </View>
              <View style={styles.heroRightBlock}>
                <TouchableOpacity
                  style={[styles.heroRankPill, { backgroundColor: c.accent + '20', borderColor: c.accent + '40' }]}
                  onPress={() => router.push('/(tabs)/rank' as any)}
                  activeOpacity={0.7}
                  accessibilityRole="button"
                  accessibilityLabel={`View rankings${rank ? `, currently #${rank.rank} of ${rank.total}` : ''}`}
                >
                  <Trophy size={14} color={c.accent} />
                  <Text style={[styles.heroRankText, { color: c.accent }]}>
                    {rank ? `#${rank.rank} of ${rank.total}` : baselineComplete ? 'Ranked' : 'Unranked'}
                  </Text>
                </TouchableOpacity>

                {/* Hide "Unranked" freshness when user has a gym rank to avoid confusion */}
                {!(rank && freshnessLabel === 'Unranked') && (
                  <TouchableOpacity
                    style={[styles.freshnessPill, { borderColor: freshnessColor + '40' }]}
                    onPress={() => setInfoSheet('freshness')}
                    activeOpacity={0.7}
                    accessibilityRole="button"
                    accessibilityLabel="Activity status info"
                  >
                    <View style={[styles.freshnessDot, { backgroundColor: freshnessColor }]} />
                    <Text style={[styles.freshnessText, { color: freshnessColor }]}>{freshnessLabel}</Text>
                    <Info size={12} color={freshnessColor} style={{ opacity: 0.7 }} />
                  </TouchableOpacity>
                )}
              </View>
            </View>

            {/* Freshness bar */}
            <View style={styles.freshBarContainer}>
              {['Fresh', 'Active', 'Aging', 'Stale'].map((band) => (
                <View
                  key={band}
                  style={[styles.freshSegment, {
                    backgroundColor: rawFreshnessLabel === band ? (BAND_COLORS[band] ?? c.accent) : c.surfaceVariant,
                    flex: 1,
                  }]}
                />
              ))}
            </View>
          </View>}

          {/* ── Score History Chart (Pro: full, Free: teaser) ── */}
          {stage !== 'newcomer' && duelScore > 0 && <ScoreHistoryChart />}

          {/* ── Baseline Checklist (new users) ── Always show until baseline is complete */}
          {baselineStatus && !baselineStatus.isComplete && (
            <BaselineChecklist status={baselineStatus} />
          )}

          {/* ── Season & Weekly Challenge ── */}
          {(stage === 'dueler' || stage === 'veteran') && <SeasonBanner />}

          {/* ── Duels ── */}
          {(stage === 'dueler' || stage === 'veteran') && <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
            <View style={styles.cardHeader}>
              <Swords size={16} color={c.accent} />
              <Text style={[styles.cardTitle, { color: c.text }]}>DUELS</Text>
              <TouchableOpacity onPress={() => setInfoSheet('duels')} style={{ padding: 2, opacity: 0.7 }} accessibilityRole="button" accessibilityLabel="What is a duel?">
                <Info size={14} color={c.textSecondary} />
              </TouchableOpacity>
              <TouchableOpacity onPress={() => router.push('/(tabs)/duels' as any)} accessibilityRole="button" accessibilityLabel="See all duels">
                <Text style={[styles.seeAll, { color: c.accent }]}>See All</Text>
              </TouchableOpacity>
            </View>

            {pendingDuels.length > 0 && (
              <TouchableOpacity
                style={[styles.duelRow, { backgroundColor: c.warning + '10', borderColor: c.warning + '30' }]}
                onPress={() => router.push('/(tabs)/duels' as any)}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel={`${pendingDuels.length} pending challenge${pendingDuels.length > 1 ? 's' : ''}`}
              >
                <View style={[styles.duelDot, { backgroundColor: c.warning }]} />
                <Text style={[styles.duelRowText, { color: c.text }]}>
                  {pendingDuels.length} pending challenge{pendingDuels.length > 1 ? 's' : ''}
                </Text>
                <ChevronRight size={14} color={c.textTertiary} />
              </TouchableOpacity>
            )}

            {activeDuels.slice(0, 3).map((duel) => {
              const hoursLeft = Math.max(0, Math.round(
                (new Date(duel.created_at).getTime() + duel.timeframe_hours * 3600000 - Date.now()) / 3600000
              ));
              return (
                <TouchableOpacity
                  key={duel.id}
                  style={[styles.duelRow, { borderColor: c.border }]}
                  onPress={() => router.push('/(tabs)/duels' as any)}
                  activeOpacity={0.7}
                >
                  <View style={[styles.duelDot, { backgroundColor: c.success }]} />
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.duelRowText, { color: c.text }]}>{duel.duel_type || 'Duel'} — Active</Text>
                    <View style={styles.duelMeta}>
                      <Clock size={11} color={c.textTertiary} />
                      <Text style={[styles.duelMetaText, { color: c.textTertiary }]}>{hoursLeft}h remaining</Text>
                    </View>
                  </View>
                  <ChevronRight size={14} color={c.textTertiary} />
                </TouchableOpacity>
              );
            })}

            {activeDuels.length === 0 && pendingDuels.length === 0 && (
              <TouchableOpacity
                style={[styles.emptyDuelCTA, { borderColor: c.accent + '40' }]}
                onPress={() => router.push({ pathname: '/(tabs)/duels', params: { start_duel: '1' } } as any)}
                activeOpacity={0.7}
              >
                <Text style={[styles.emptyDuelText, { color: c.textSecondary }]}>No active duels</Text>
                <Text style={[styles.emptyDuelAction, { color: c.accent }]}>{'Challenge someone \u2192'}</Text>
              </TouchableOpacity>
            )}
          </View>}

          {/* Smart CTA removed — redundant with Duels section */}

          {/* ── DUEL Program Card (Pro+ only) ── */}
          {hasAtLeast(tier, 'pro') && (() => {
            const todayDay = coachPlan
              ? coachPlan.days[new Date().getDay() % Math.max(1, coachPlan.days.length)]
              : null;
            return (
              <TouchableOpacity
                style={[styles.card, { backgroundColor: c.surface, borderColor: c.border, padding: 14 }]}
                onPress={() => router.push((coachPlan ? '/duel-coach-plan' : '/duel-coach') as any)}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel="DUEL Program"
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                  <Dumbbell size={18} color={c.accent} />
                  <View style={{ flex: 1 }}>
                    <Text style={{ color: c.text, fontFamily: TYPO.subheading.bold, fontSize: FONT.sm, textTransform: 'uppercase', letterSpacing: 0.5 }}>DUEL Program</Text>
                    {todayDay ? (
                      <Text style={{ color: c.textSecondary, fontSize: FONT.xs, marginTop: 2 }}>
                        {'Today: '}{todayDay.focus}{' · '}{todayDay.exercises.length}{' exercises · ~'}{todayDay.estimatedMinutes}{'min'}
                      </Text>
                    ) : (
                      <Text style={{ color: c.textSecondary, fontFamily: TYPO.body.regular, fontSize: FONT.xs, marginTop: 2 }}>Personalized weekly training plans</Text>
                    )}
                  </View>
                  <ChevronRight size={14} color={c.textTertiary} />
                </View>
                {todayDay && (
                  <View style={{ marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: c.border }}>
                    <Pressable
                      style={{ backgroundColor: c.accent, borderRadius: 2, paddingVertical: 8, alignItems: 'center' }}
                      onPress={() => {
                        router.push({ pathname: '/(tabs)/record', params: { coachDayJson: JSON.stringify(todayDay) } } as any);
                      }}
                      accessibilityRole="button"
                      accessibilityLabel="Start today's workout"
                      android_ripple={{ color: 'rgba(0,0,0,0.15)', borderless: false }}
                      hitSlop={{ top: 4, bottom: 4 }}
                    >
                      <Text style={{ color: c.accentForeground, fontSize: 12, fontWeight: '800', letterSpacing: 1 }}>START TODAY&apos;S WORKOUT</Text>
                    </Pressable>
                  </View>
                )}
              </TouchableOpacity>
            );
          })()}

          {/* Quick Actions removed — redundant with tab bar */}

          {/* ── Live Feed ── */}
          {(stage === 'dueler' || stage === 'veteran') && (
            <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
              <View style={styles.cardHeader}>
                <BarChart3 size={16} color={c.accent} />
                <Text style={[styles.cardTitle, { color: c.text }]}>LIVE FEED</Text>
              </View>
              <LiveFeed />
            </View>
          )}

          {/* ── Quick Links ── */}
          {(stage === 'dueler' || stage === 'veteran') && (
            <View style={{ flexDirection: 'row', gap: SPACE.sm }}>
              <TouchableOpacity
                style={[styles.card, { backgroundColor: c.surface, borderColor: c.border, flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, padding: 14 }]}
                onPress={() => router.push('/tournaments' as any)}
                activeOpacity={0.7}
              >
                <Trophy size={16} color={c.accent} />
                <Text style={[{ color: c.text, fontFamily: TYPO.body.semibold, fontSize: FONT.sm }]}>Tournaments</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.card, { backgroundColor: c.surface, borderColor: c.border, flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, padding: 14 }]}
                onPress={() => router.push('/rulebook' as any)}
                activeOpacity={0.7}
              >
                <BarChart3 size={16} color={c.accent} />
                <Text style={[{ color: c.text, fontFamily: TYPO.body.semibold, fontSize: FONT.sm }]}>Rulebook</Text>
              </TouchableOpacity>
            </View>
          )}
          {stage === 'veteran' && (
            <TouchableOpacity
              style={[styles.card, { backgroundColor: c.surface, borderColor: c.border, flexDirection: 'row', alignItems: 'center', gap: 8, padding: 14 }]}
              onPress={() => router.push('/judge-queue' as any)}
              activeOpacity={0.7}
            >
              <Users size={16} color={c.accent} />
              <Text style={[{ color: c.text, fontFamily: TYPO.body.semibold, fontSize: FONT.sm }]}>Judge Queue</Text>
            </TouchableOpacity>
          )}
          {stage === 'veteran' && (
            <TouchableOpacity
              style={[styles.card, { backgroundColor: c.surface, borderColor: c.border, flexDirection: 'row', alignItems: 'center', gap: 8, padding: 14 }]}
              onPress={() => router.push('/teams' as any)}
              activeOpacity={0.7}
            >
              <Users size={16} color={c.accent} />
              <Text style={[{ color: c.text, fontFamily: TYPO.body.semibold, fontSize: FONT.sm }]}>Teams & Crews</Text>
            </TouchableOpacity>
          )}

          {/* ── Coach / Org Widgets ── */}
          {tier === 'coach_pro' && isFlagEnabled('COACH_DASHBOARD_ENABLED') && <CoachHomeWidget />}
          {tier === 'org_pro' && <OrgHomeWidget />}

          <View style={{ height: 40 }} />
        </ScrollView>
      </Animated.View>

      <CoachInviteModal invites={coachInvites} onAccept={acceptInvite} onDecline={declineInvite} />
      <TutorialOverlay screenKey="home" />
      <ClientMessageModal visible={showMessages} onClose={() => setShowMessages(false)} />
      <WelcomeProModal visible={showWelcomePro} onDismiss={markWelcomeProSeen} />
      {infoSheet && (
        <InfoSheet
          visible={true}
          onClose={() => setInfoSheet(null)}
          title={INFO_CONTENT[infoSheet].title}
          content={INFO_CONTENT[infoSheet].content}
        />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { paddingHorizontal: SPACE.lg, paddingTop: SPACE.md, paddingBottom: 64, gap: SPACE.lg },
  greeting: { fontSize: FONT.xl, fontFamily: TYPO.heading.bold, letterSpacing: -0.3 },
  badge: {
    position: 'absolute', top: 2, right: 2, backgroundColor: '#ef4444', borderRadius: 4,
    minWidth: 16, height: 16, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 4,
  },
  badgeText: { color: '#fff', fontSize: 10, fontWeight: '800' },

  // Hero
  heroCard: { borderWidth: 1, borderRadius: RADIUS.xs, padding: SPACE.lg, gap: SPACE.lg },
  heroTop: { flexDirection: 'row', justifyContent: 'space-between' },
  heroScoreBlock: { flex: 1 },
  scoreHeader: { flexDirection: 'row', alignItems: 'center', gap: SPACE.xs },
  heroScoreLabel: { ...SECTION_LABEL, fontFamily: TYPO.subheading.bold },
  infoIcon: { minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center', opacity: 0.7 },
  heroScoreValue: { fontSize: FONT['4xl'], fontFamily: TYPO.heading.bold, letterSpacing: -1, lineHeight: 54 },
  rankContext: { fontSize: FONT.xs, fontFamily: TYPO.body.medium, marginTop: 2 },
  heroRightBlock: { alignItems: 'flex-end', gap: SPACE.sm },
  heroRankPill: { flexDirection: 'row', alignItems: 'center', gap: SPACE.xs, paddingVertical: SPACE.xs, paddingHorizontal: SPACE.sm, borderRadius: RADIUS.xs, borderWidth: 1 },
  heroRankText: { fontSize: FONT.sm, fontFamily: TYPO.subheading.bold, textTransform: 'uppercase' },
  freshnessPill: { flexDirection: 'row', alignItems: 'center', gap: SPACE.xs, paddingVertical: SPACE.xs, paddingHorizontal: SPACE.sm, borderRadius: RADIUS.xs, borderWidth: 1 },
  freshnessDot: { width: 7, height: 7, borderRadius: 4 },
  freshnessText: { fontSize: FONT.xs, fontFamily: TYPO.subheading.semibold, textTransform: 'uppercase' },
  freshBarContainer: { flexDirection: 'row', borderRadius: RADIUS.xs, overflow: 'hidden', height: 4, gap: 2 },
  freshSegment: { height: 4, borderRadius: RADIUS.xs },

  // CTA
  ctaBanner: { flexDirection: 'row', alignItems: 'center', gap: SPACE.sm, paddingVertical: SPACE.sm, paddingHorizontal: SPACE.base, borderRadius: RADIUS.xs, borderWidth: 1 },
  ctaText: { flex: 1, fontSize: FONT.sm, fontFamily: TYPO.subheading.semibold, textTransform: 'uppercase' },

  // Quick Actions
  quickActions: { flexDirection: 'row', gap: SPACE.xs },
  quickActionBtn: { flex: 1, alignItems: 'center', gap: SPACE.xs, paddingVertical: SPACE.md, paddingHorizontal: SPACE.xs, borderRadius: RADIUS.none, borderWidth: 1 },
  quickActionLabel: { fontSize: 10, fontFamily: TYPO.subheading.bold, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 0.5 },

  // Cards
  card: { ...CARD.standard, padding: SPACE.lg, gap: SPACE.md },
  cardHeader: { flexDirection: 'row', alignItems: 'center', gap: SPACE.sm },
  cardTitle: { ...SECTION_LABEL, fontFamily: TYPO.subheading.bold, flex: 1 },
  seeAll: { fontSize: FONT.xs, fontFamily: TYPO.subheading.bold, textTransform: 'uppercase', letterSpacing: TRACKING.wide },

  // Duels
  duelRow: { flexDirection: 'row', alignItems: 'center', gap: SPACE.sm, paddingVertical: SPACE.sm, paddingHorizontal: SPACE.sm, borderRadius: RADIUS.xs, borderWidth: 1 },
  duelDot: { width: 8, height: 8, borderRadius: 4 },
  duelRowText: { fontSize: FONT.base, fontFamily: TYPO.body.semibold },
  duelMeta: { flexDirection: 'row', alignItems: 'center', gap: SPACE.xs, marginTop: 2 },
  duelMetaText: { fontSize: FONT.xs, fontFamily: TYPO.body.medium },
  emptyDuelCTA: { paddingVertical: SPACE.lg, alignItems: 'center', gap: SPACE.xs, borderRadius: RADIUS.xs, borderWidth: 1, borderStyle: 'solid' },
  emptyDuelText: { fontSize: FONT.sm, fontFamily: TYPO.body.medium },
  emptyDuelAction: { fontSize: FONT.sm, fontFamily: TYPO.subheading.bold, textTransform: 'uppercase' },

  // Welcome card (newcomer)
  welcomeCard: { borderWidth: 1, borderRadius: RADIUS.xs, padding: SPACE.lg, gap: SPACE.lg, alignItems: 'center' as const },
  welcomeHeading: { fontSize: FONT['2xl'], fontFamily: TYPO.heading.bold, letterSpacing: TRACKING.wider, textTransform: 'uppercase' as const },
  welcomeSteps: { gap: SPACE.md, width: '100%' as unknown as number },
  welcomeStep: { flexDirection: 'row' as const, alignItems: 'center' as const, gap: SPACE.sm },
  welcomeStepNum: { width: 28, height: 28, borderRadius: RADIUS.xs, alignItems: 'center' as const, justifyContent: 'center' as const },
  welcomeStepNumText: { color: '#030303', fontSize: FONT.base, fontFamily: TYPO.heading.bold },
  welcomeStepLabel: { fontSize: FONT.md, fontFamily: TYPO.subheading.bold, letterSpacing: TRACKING.normal, textTransform: 'uppercase' as const },
  welcomeCTA: { flexDirection: 'row' as const, alignItems: 'center' as const, justifyContent: 'center' as const, gap: SPACE.sm, backgroundColor: '#B8972E', paddingVertical: 16, paddingHorizontal: SPACE.xl, width: '100%' as unknown as number, borderRadius: RADIUS.none },
  welcomeCTAText: { color: '#030303', fontSize: FONT.md, fontFamily: TYPO.heading.bold, letterSpacing: TRACKING.wide, textTransform: 'uppercase' as const },
});

